import { deriveCreatureBodyAppearance } from '../authoring/creature-appearance';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { surfaceField } from '../compile/from-base-encounter-media';
import { fromBaseClone, fromBaseRecord, fromBaseUuid } from '../compile/from-base-context';
import { definitionVitalityMaximum } from './vitality';
import { SIZE_WIDTHS_FT } from '../inventory/lift-occupancy';
import { fabricateRows } from '../actions/ability-preconditions';
import { isContainer } from '../inventory/move-rules';
import type { SummonPlan, SummonPoint } from '../ability/summon';
import { admitSpawnRowPlan, spawnRowContracts } from './row-value-contract';
import { sha256 } from '../port/k4-dispatch/support-b2';
import { summonArrival, type SummonArrival } from './summon-arrival';
import { posCoord } from '../../shared/array-types';
import type { InventoryBirthOptions } from '../inventory/birth-inputs-20';
import type { SpawnSourcePackage } from './source-plan';
import type { AppliedNewEntity } from '../transport/applied-frame';

export type Row = Record<string, unknown>;
export type SpawnTable = 'encounter_creatures' | 'inventory' | 'statuses' | 'charges' | 'pictures' | 'encounter_inventory' | 'inventory_lock_bindings'
    | 'encounter_creature_turn_resets' | 'encounter_creatures_spells_prepared'
    | 'enchantment_templates' | 'enchantment_template_enchantments'
    | 'encounters' | 'campaigns' | 'encounter_creature_triggers' | 'corpse_inventory' | 'corpse_links' | 'death_loot' | 'death_part_statuses' | 'cast_inventory'
    | 'death_inventory_pictures' | 'encounter_creature_swarm_states' | 'swarm_pile_links';
export interface SpawnWrite { table: SpawnTable; op: 'insert' | 'update'; row: Row; before?: Row }
export interface SpawnLog { kind: 'added_creatures' | 'add_inventory' | 'summoned_creature' | 'creature_transform' | 'janus_unported_skip'; name: string; detail: string; actor_id?: string; arrival?: SummonArrival }
export interface SpawnRowPlan { version: 'janus.spawn.rows.v1'; writes: SpawnWrite[]; logs?: SpawnLog[]; readonly new_entities?: readonly AppliedNewEntity[]; readonly new_entity_inputs?: readonly SpawnSourcePackage[] }
export interface SpawnReaders {
    value(row: Row, key: string): unknown;
    entries(row: Row, key: string): unknown[];
    ownEntries(row: Row, key: string): unknown[];
    providers(row: Row): Row[];
    /** Complete browser compile, including the monster-template stamp. No PHP result or prior creature copy. */
    creature(itemId: string, monsterTemplateId: string | null, draft?: Row): Row;
    item(itemId: string): Row;
    uuid(): string;
    die(sides: number): number;
    name(source: Row): string | null;
    /** add_inventory's complete creation tree (size/enchantment, packed/prefab contents and spoilage). */
    inventory(source: Row, holder: Row, qty: number, templateId: string, distinct?: boolean, options?: InventoryBirthOptions): SpawnRowPlan;
    equip(holder: Row, inventory: Row, slot?: string): string | null;
    loot(source: Row, holder: Row): SpawnRowPlan;
    ground(source: Row, point: SummonPoint, direct: boolean, roster: Row[], unpointed?: boolean): SummonPoint;
    enemyTeam: string;
    walkingSpeed: string;
    hiddenStatus: string;
    summonTag: string;
    etherId: string;
    clock?(): { campaignId: string; starts: string } | null;
    casterDeathStatusIds?: readonly string[];
}
export interface CreatureSpawnRequest {
    itemId: string; monsterTemplateId?: string | null; draft?: Row; qty?: number;
    vitality?: 'average' | 'max' | 'random'; team?: string; controllerId?: string;
    summonType?: string | null; point?: SummonPoint | null; floorPoint?: boolean; hidden?: boolean; private?: number;
    name?: string; appearance?: Row | null; loot?: boolean; instanceId?: string; logAddition?: boolean;
}
/** Decide at the coordinate columns' precision before any held base or save is published. */
export function storedSpawnPoint(point: SummonPoint): SummonPoint {
    return { x: Number(point.x.toFixed(3)), y: Number(point.y.toFixed(3)), z: Number(point.z.toFixed(3)) };
}
export interface TransformSpawnRequest extends CreatureSpawnRequest {
    victimId: string; equipInventory?: boolean; dropInventory?: boolean;
}
export const emptySpawnPlan = (): SpawnRowPlan => ({ version: 'janus.spawn.rows.v1', writes: [], logs: [] });
export const spawnPlanHasEffects = (plan: SpawnRowPlan | undefined): boolean => !!plan && (plan.writes.length > 0 || (plan.logs?.length ?? 0) > 0);
/** Inventory log participants never require a creature revision, including newly minted loot. */
export function spawnInventoryIds(plan: SpawnRowPlan | undefined): string[] {
    const schema = spawnRowContracts;
    // A write row is the flat table row; ft_browser_spawn_inventory_ids() reads it as ['inventory' => row].
    return [...new Set((plan?.writes ?? []).filter(write => (schema[write.table]?.table ?? write.table) === 'inventory')
        .map(write => String(readInventoryField({ inventory: write.row }, 'inventory_id') ?? '').toUpperCase()))];
}
/** Stable lair/receipt identity arbitrates two GM observers without a PHP rule check. */
export function spawnRowIdentity(key: string): string {
    const hash = sha256('c5-spawn|' + key);
    return `${hash.slice(0, 8)}-${hash.slice(8, 12)}-4${hash.slice(13, 16)}-8${hash.slice(17, 20)}-${hash.slice(20, 32)}`.toUpperCase();
}
export function mergeSpawnPlans(...plans: Array<SpawnRowPlan | undefined>): SpawnRowPlan {
    const result = emptySpawnPlan(), rows = new Map<string, SpawnWrite>();
    Object.defineProperty(result, 'new_inventory', { configurable: true, get: () => plans.flatMap(plan => plan?.new_inventory ?? []) });
    const schema = spawnRowContracts;
    for (const plan of plans) if (plan) {
        (result.logs ??= []).push(...plan.logs ?? []);
        for (const write of plan.writes) {
            const primary = schema[write.table]?.key;
            if (!primary) throw new Error(`spawn:row-kind:${write.table}`);
            const key = write.table + ':' + String(readItemField(write.row, primary) ?? '').toUpperCase(), previous = rows.get(key);
            if (previous) {
                if (previous.op !== 'update' || write.op !== 'update') throw new Error('spawn:duplicate-insert');
                previous.row = { ...previous.row, ...write.row }; previous.before = { ...write.before, ...previous.before };
            } else { const copy = fromBaseClone(write); rows.set(key, copy); result.writes.push(copy); }
        }
    }
    Object.defineProperty(result, 'new_entity_inputs', { configurable: true, get: () => plans.flatMap(plan => plan?.new_entity_inputs ?? []) });
    Object.defineProperty(result, 'new_entities', { get: () => plans.flatMap(plan => plan?.new_entities ?? []), configurable: true });
    Object.defineProperty(result, 'new_inventory', { get: () => plans.flatMap(plan => plan?.new_inventory ?? []), configurable: true });
    const preparationFailures = new Map<SpawnRowPlan, unknown>();
    Object.defineProperty(result, 'prepare_new_entities', { value: async () => {
        await Promise.all(plans.map(async plan => {
            try { await plan?.prepare_new_entities?.(); }
            catch (error) { if (plan) preparationFailures.set(plan, error); throw error; }
        }));
    }, configurable: true });
    Object.defineProperty(result, 'defer_new_entities', { configurable: true, value: (error: unknown) => {
        const reasons: string[] = [];
        for (const plan of plans) if (plan) {
            if (preparationFailures.has(plan)) {
                const reason = plan.defer_new_entities?.(preparationFailures.get(plan));
                if (reason) reasons.push(reason);
                preparationFailures.delete(plan);
                continue;
            }
            try { void plan.new_entities; void plan.new_inventory; }
            catch (failure) { if (!plan.defer_new_entities) throw failure; reasons.push(plan.defer_new_entities(failure ?? error)); }
        }
        return reasons.join(';');
    } });
    return admitSpawnRowPlan(result);
}
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
const ec = (r: Row): Row => fromBaseRecord(r.encounter_creatures) ? r.encounter_creatures : r;
const saved = (r: Row, key: string): unknown => surfaceField(ec(r), key);
const positionOf = (r: Row): SummonPoint => {
    const at = fromBaseRecord(r.position) || fromBaseRecord(r.pos_arr) ? r : ec(r);
    return { x: posCoord(at, 'x_pos') ?? 0, y: posCoord(at, 'y_pos') ?? 0, z: posCoord(at, 'z_pos') ?? 0 };
};
const int = (v: unknown): number => Math.trunc(Number(v ?? 0));
const idOf = (r: Row): string => upper(saved(r, 'encounter_creature_id'));
const entriesKey = (entry: unknown): string => {
    const row = fabricateRows([entry])[0];
    return row ? row.uuid.toUpperCase() + '|' + row.template.toUpperCase() : '';
};

/** _ft_template_equipped_inventory_work: exactly one source, composite identity dedupe. */
export function spawnEquippedEntries(source: Row, read: SpawnReaders): unknown[] {
    const own = read.ownEntries(source, 'inventory_equipped');
    const override = fromBaseRecord(source.computed) && fromBaseRecord(source.computed.mt)
        && fromBaseRecord(source.computed.mt.direct_loadout_override) && source.computed.mt.direct_loadout_override.inventory_equipped;
    const sources = [{ id: upper(readItemField(source, 'item_id')), entries: own },
        ...read.providers(source).map(row => ({ id: upper(readItemField(row, 'item_id')), entries: read.entries(row, 'inventory_equipped') }))]
        .filter(row => row.entries.length);
    const selected = readLayerListEntryUuid(read.ownEntries(source, 'inventory_equipped_source')[0]);
    const list = override ? own : sources.find(row => row.id === upper(selected))?.entries ?? sources[0]?.entries ?? [];
    const seen = new Set<string>();
    return list.filter(entry => {
        const key = entriesKey(entry);
        if (!fromBaseUuid(key.split('|')[0]) || seen.has(key)) return false;
        seen.add(key); return true;
    });
}

export function appendSpawnStatus(plan: SpawnRowPlan, read: Pick<SpawnReaders, 'uuid' | 'clock'>, target: string, caster: string, item: string,
    origin = '', tag: SummonPlan['tag'] = null): void {
    const ends = (tag?.ends ?? '0-0-0-0-0-0').split('-').map(Number);
    const clock = read.clock?.(), starts = (clock?.starts ?? '0-0-0-0-0-0').split('-').map(Number);
    plan.writes.push({ table: 'statuses', op: 'insert', row: {
        status_id: read.uuid(), status_target_id: target, status_caster_id: caster,
        status_item_id: item, status_origin_id: origin || null, status_connected_id: tag?.connection || read.uuid(),
        status_casting_targets_arr: '', status_stack: 1,
        status_campaign_id: clock?.campaignId || null,
        status_origin_point_x: 0, status_origin_point_z: 0, status_distance_point_x: 0, status_distance_point_z: 0,
        status_aoe_distance: 0, status_aoe_distance_depth: 0, status_aoe_distance_height: 0,
        ...Object.fromEntries(['year', 'month', 'day', 'hour', 'minute', 'second'].map((unit, i) => ['status_starts_campaign_' + unit, starts[i] ?? 0])),
        ...Object.fromEntries(['year', 'month', 'day', 'hour', 'minute', 'second'].map((unit, i) => ['status_ends_campaign_' + unit, ends[i] ?? 0])),
        status_ends_on_target_start_of_turn: tag?.flags[0] ?? 0, status_ends_on_target_end_of_turn: tag?.flags[1] ?? 0,
        status_ends_on_caster_start_of_next_turn: tag?.flags[2] ?? 0, status_ends_on_caster_end_of_next_turn: tag?.flags[3] ?? 0,
        status_private: 0, status_deleted: 0,
    } });
}

/** Committed native departures use the same identity list in turn and calendar delivery. */
export function spawnDepartureIds(plan: SpawnRowPlan | undefined): string[] {
    return [...new Set((plan?.writes ?? []).filter(write => write.table === 'encounter_creatures'
        && Number(readItemField(write.row, 'encounter_creature_deleted') ?? 0) === 1)
        .map(write => upper(readItemField(write.row, 'encounter_creature_id'))))];
}

function inventoryNodes(holder: Row): Row[] {
    const nodes: Row[] = [], seen = new Set<string>();
    const walk = (list: unknown): void => {
        if (!Array.isArray(list)) return;
        for (const node of list) if (fromBaseRecord(node)) {
            const id = upper(readInventoryField(node, 'inventory_id'));
            if (id && !seen.has(id)) { seen.add(id); nodes.push(node); walk(node.inventory_in_container); }
        }
    };
    walk(holder.inventory); return nodes;
}
/** What a transform's replacement re-creates: the victim's OWN possessions, as planTransformRows walks them. */
function ownKitNodes(victim: Row): Row[] {
    const oldId = idOf(victim);
    return inventoryNodes(victim).filter(node => upper(readInventoryField(node, 'possess_id')) === oldId);
}
/** The kit definitions a transform compiles through its spawn package. The package must be requested WITH them, or each item
 *  lacks its video / layer inputs and hands back `spawn:item-compile:definition-video-input` (fix-bug genocadcat). */
export function transformKitItemIds(victim: Row): string[] {
    return [...new Set(ownKitNodes(victim).map(node => upper(readInventoryField(node, 'inventory_item_id'))).filter(fromBaseUuid))];
}
function appendInventory(plan: SpawnRowPlan, holder: Row, addition: SpawnRowPlan): void {
    plan.writes.push(...addition.writes);
    (plan.logs ??= []).push(...addition.logs ?? []);
    const list = Array.isArray(holder.inventory) ? holder.inventory as Row[] : (holder.inventory = []) as Row[];
    for (const write of addition.writes) if (write.table === 'inventory' && write.op === 'insert') list.push({ inventory: write.row });
}
function loadout(plan: SpawnRowPlan, source: Row, holder: Row, read: SpawnReaders): void {
    for (const [list, equip] of [[spawnEquippedEntries(source, read), true], [read.entries(source, 'inventory_unequipped'), false]] as const) {
        for (const entry of list) {
            const resolved = fabricateRows([entry])[0];
            if (!resolved) continue;
            const addition = read.inventory(read.item(resolved.uuid), holder, equip ? 1 : resolved.qty, resolved.template, equip);
            appendInventory(plan, holder, addition);
            if (equip) {
                const first = addition.writes.find(write => write.table === 'inventory' && write.op === 'insert');
                if (first) first.row.equipped = read.equip(holder, { inventory: first.row }) ?? '';
                if (resolved.qty > 1) appendInventory(plan, holder, read.inventory(read.item(resolved.uuid), holder, resolved.qty - 1, resolved.template));
            }
        }
    }
    // place_ammo_in_matching_containers: first matching top-level container, before random loot.
    const containers = new Map<string, string>(), nodes = inventoryNodes(holder), holderId = idOf(holder);
    for (const node of nodes) {
        const item = read.item(String(readInventoryField(node, 'inventory_item_id')));
        const classification = readLayerListEntryUuid(read.value(item, 'weapon_ammo_classification'));
        if (upper(readInventoryField(node, 'possess_id')) === holderId && classification
            && isContainer(item, { value: (row, key) => row ? read.value(row, key) : null,
                itemType: row => String(readItemField(row ?? null, 'item_type') ?? ''),
                inventoryField: (row, key) => readInventoryField(row ?? null, key), statusItemIds: () => [] })
            && !containers.has(classification)) containers.set(classification, String(readInventoryField(node, 'inventory_id')));
    }
    for (const node of nodes) {
        const item = read.item(String(readInventoryField(node, 'inventory_item_id')));
        if (readItemField(item, 'item_type') !== 'ammo' || upper(readInventoryField(node, 'possess_id')) !== holderId) continue;
        const parent = containers.get(readLayerListEntryUuid(read.value(item, 'weapon_ammo_classification')));
        if (parent) (node.inventory as Row).possess_id = parent;
    }
}

/** insert_encounter_creature + the GM add loop. It never mutates its captured inputs. */
export function planCreatureSpawn(encounterId: string, encounter: Row, request: CreatureSpawnRequest, read: SpawnReaders, newbornIds: readonly string[] = []): SpawnRowPlan {
    if (!fromBaseUuid(encounterId) || !fromBaseUuid(request.itemId)) throw new Error('spawn:identity');
    const source = read.creature(request.itemId, request.monsterTemplateId ?? null, request.draft);
    const type = readItemField(source, 'item_type');
    if (type !== 'creature' && type !== 'lair') throw new Error('spawn:creature-classification-required');
    const count = type === 'lair' ? 1 : request.qty ?? 1;
    if (!Number.isSafeInteger(count) || count < 1 || count > 100) throw new Error('spawn:quantity');
    if (request.instanceId && (count !== 1 || !fromBaseUuid(request.instanceId))) throw new Error('spawn:instance-identity');
    const roster = [...Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : []];
    const plan = emptySpawnPlan(), controller = upper(request.controllerId), summon = controller !== '';
    const current = roster.filter(row => int(saved(row, 'encounter_creature_deleted')) === 0);
    let number = Math.max(0, ...current.map(row => int(saved(row, 'creature_number'))));
    const initiatives = current.filter(row => Boolean(saved(row, 'controller_encounter_creature_id')) === summon)
        .map(row => saved(row, 'creature_initiative')).filter(value => value !== null && value !== undefined).map(Number);
    let initiative = initiatives.length ? Math.min(...initiatives) : summon ? 5000 : 10000;
    const cols = Math.ceil(Math.sqrt(count)), rows = Math.ceil(count / cols);
    const width = SIZE_WIDTHS_FT[upper(readLayerListEntryUuid(read.value(source, 'size')))] ?? 5;
    for (let i = 0; i < count; i++) {
        const name = request.name || read.name(source) || readItemField(source, 'item_name') || 'Unknown';
        const id = request.instanceId ?? newbornIds[i] ?? read.uuid(), picCandidates = fromBaseRecord(source.pictures) && Array.isArray(source.pictures.item) ? source.pictures.item as Row[] : [];
        const pic = picCandidates.length ? readItemField(picCandidates[read.die(picCandidates.length) - 1]!, 'picture_id') : null;
        const voices = [...new Set(read.entries(source, 'default_voice_id').map(readLayerListEntryUuid).filter(fromBaseUuid).map(upper))];
        const maximum = definitionVitalityMaximum(source, read, request.vitality);
        const spirit = int(read.value(source, 'spirit'));
        const position = request.point ? { x: request.point.x + ((i % cols) - (cols - 1) / 2) * width,
            y: request.point.y + (Math.floor(i / cols) - (rows - 1) / 2) * width, z: request.point.z }
            : { x: (i + 1) * 2.5, y: 0, z: 0 };
        const at = storedSpawnPoint(read.ground(source, position, request.floorPoint ?? !!request.point, roster, !request.point));
        const row: Row = {
            encounter_creature_id: id, encounter_id: upper(encounterId), item_id: upper(request.itemId), item_type: type,
            encounter_creature_monster_template_id: request.monsterTemplateId || null,
            encounter_creature_name: name,
            encounter_creature_private: type === 'lair' || request.private === 1 ? 1 : 0,
            encounter_creature_deleted: 0, encounter_creature_dead: 0,
            team: request.team || readLayerListEntryUuid(read.value(source, 'default_team')) || read.enemyTeam,
            controller_encounter_creature_id: controller || null, summon_type: request.summonType || null,
            creature_number: ++number, creature_initiative: Math.min(--initiative, summon ? 4999 : 9999),
            v_current: maximum, v_max: maximum, v_temp: int(read.value(source, 'v_temp')), v_max_temp: int(read.value(source, 'v_max_temp')),
            aps: type === 'lair' ? 1 : Math.max(3, int(read.value(source, 'aps'))), aps_total: type === 'lair' ? 1 : Math.max(3, int(read.value(source, 'aps'))),
            reaction: 1, mana: spirit, spirit, luck: int(read.value(source, 'luck')), weapon_attack: 0, head_attack: 0,
            hunger_meter_current: 0, thirst_meter_current: 0, movement_pct: 0, spell_list_prepared: 0,
            x_pos: at.x, y_pos: at.y, z_pos: at.z,
            preferred_speed_type: readLayerListEntryUuid(read.value(source, 'default_preferred_speed_type')) || read.walkingSpeed,
            encounter_creature_presentation_pic_id: pic, encounter_creature_thumbnail_pic_id: pic,
            gender: readLayerListEntryUuid(read.value(source, 'gender')) || null,
            voice_id: voices.length ? voices[read.die(voices.length) - 1] : null,
            merchant_favor: read.value(source, 'merchant') ? 50 : null,
        };
        if (type === 'lair') row.lair_dc = 10;
        const owner = current.find(row => idOf(row) === controller);
        if (summon && owner && fromBaseUuid(saved(owner, 'combat_id'))) row.combat_id = saved(owner, 'combat_id');
        if (request.appearance) {
            row.appearance = JSON.stringify(request.appearance);
            const body = deriveCreatureBodyAppearance(request.appearance);
            if (body) row.body_appearance = JSON.stringify(body);
        }
        plan.writes.push({ table: 'encounter_creatures', op: 'insert', row });
        const holder = { ...source, encounter_creatures: row, inventory: [] };
        loadout(plan, source, holder, read);
        if (request.loot !== false && !summon && type !== 'lair') {
            appendInventory(plan, holder, read.loot(source, holder));
        }
        if (request.hidden) appendSpawnStatus(plan, read, id, id, read.hiddenStatus);
        if (!summon && request.logAddition !== false) (plan.logs ??= []).push({ kind: 'added_creatures', name: String(row.encounter_creature_name), detail: '', actor_id: id });
        roster.push(holder);
    }
    return admitSpawnRowPlan(plan);
}

/** A replacement is fully planned before any old row is retired. */
export function planTransformRows(encounterId: string, encounter: Row, request: TransformSpawnRequest, read: SpawnReaders): SpawnRowPlan {
    const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
    const victim = roster.find(row => idOf(row) === upper(request.victimId));
    if (!victim) throw new Error('spawn:transform-victim-not-held');
    if (saved(victim, 'item_type') === 'player_character') throw new Error('spawn:player-replacement-plan-required');
    const point = positionOf(victim);
    const remaining = fromBaseClone(roster.filter(row => idOf(row) !== idOf(victim)));
    const count = request.qty ?? 1;
    if (!Number.isSafeInteger(count) || count < 1 || count > 10) throw new Error('spawn:transform-quantity');
    const plan = emptySpawnPlan();
    // Native split placement advances X by 1.5 feet, planning each replacement against prior spawns.
    for (let i = 0; i < count; i++) {
        const addition = planCreatureSpawn(encounterId, { ...encounter, encounter_creatures_arr: remaining }, { ...request,
            qty: 1, private: 0, point: { ...point, x: point.x + i * 1.5 },
            team: request.team || readLayerListEntryUuid(saved(victim, 'team')), loot: false, logAddition: false }, read);
        plan.writes.push(...addition.writes); (plan.logs ??= []).push(...addition.logs ?? []);
        remaining.push(...addition.writes.filter(write => write.table === 'encounter_creatures' && write.op === 'insert').map(write => ({ encounter_creatures: write.row })));
    }
    const newRows = plan.writes.filter(write => write.table === 'encounter_creatures' && write.op === 'insert').map(write => write.row);
    const oldId = idOf(victim), nodes = inventoryNodes(victim);
    const first = newRows[0];
    if (!first) throw new Error('spawn:transform-empty');
    const holder: Row = { ...read.creature(request.itemId, request.monsterTemplateId ?? null), encounter_creatures: first,
        inventory: plan.writes.filter(write => write.table === 'inventory' && write.op === 'insert' && upper(readInventoryField(write.row, 'possess_id')) === upper(readItemField(first, 'encounter_creature_id'))).map(write => ({ inventory: write.row })) };
    if (!request.dropInventory) for (const node of ownKitNodes(victim)) {
        const worn = readLayerListEntryUuid(readInventoryField(node, 'equipped'));
        const copies = read.inventory(read.item(String(readInventoryField(node, 'inventory_item_id'))), holder, Number(readInventoryField(node, 'item_qty') ?? 1), '', request.equipInventory && !!worn);
        appendInventory(plan, holder, copies);
        if (request.equipInventory && worn) {
            for (const write of plan.writes) if (write.table === 'inventory' && write.op === 'insert'
                && upper(readInventoryField(write.row, 'possess_id')) === upper(readItemField(first, 'encounter_creature_id'))
                && readInventoryField(write.row, 'equipped') === worn) write.row.equipped = '';
            const copy = copies.writes.find(write => write.table === 'inventory');
            if (copy) copy.row.equipped = read.equip(holder, { inventory: copy.row }, worn) ?? '';
        }
    }
    for (const node of nodes) {
        const id = readInventoryField(node, 'inventory_id');
        if (!fromBaseUuid(id)) throw new Error('spawn:transform-inventory-identity');
        plan.writes.push({ table: 'inventory', op: 'update', row: request.dropInventory ? { inventory_id: id, possess_id: encounterId, equipped: null,
            x_pos: point.x, y_pos: point.y, z_pos: point.z } : { inventory_id: id, inventory_deleted: 1 },
            before: { inventory_deleted: int(readInventoryField(node, 'inventory_deleted')), possess_id: String(readInventoryField(node, 'possess_id') ?? ''),
                item_qty: Number(readInventoryField(node, 'item_qty') ?? 1) } });
    }
    plan.writes.push({ table: 'encounter_creatures', op: 'update', row: { encounter_creature_id: oldId, encounter_creature_deleted: 1,
        encounter_creature_dead: 1, v_current: 0, v_temp: 0 }, before: { encounter_creature_deleted: int(saved(victim, 'encounter_creature_deleted')),
            movement_rev: int(saved(victim, 'movement_rev')), encounter_id: String(saved(victim, 'encounter_id') ?? '') } });
    const status = fromBaseRecord(victim.statuses) ? victim.statuses : {};
    // Target-side retirements. Caster-side death/concentration cascades are supplied by the shared status planner.
    for (const row of Array.isArray(status.target) ? status.target as Row[] : []) {
        const id = readItemField(row, 'status_id');
        if (fromBaseUuid(id)) plan.writes.push({ table: 'statuses', op: 'update', row: { status_id: id, status_deleted: 1 }, before: { status_deleted: int(readItemField(row, 'status_deleted')) } });
    }
    const raw = fromBaseRecord(victim._entity_base) && fromBaseRecord(victim._entity_base.base) ? victim._entity_base.base : victim;
    for (const table of ['pictures', 'encounter_creatures_spells_prepared'] as const) {
        if (!Array.isArray(raw[table])) continue;
        const key = table === 'pictures' ? 'picture_id' : 'encounter_creature_spell_prepared_i';
        const deleted = table === 'pictures' ? 'picture_deleted' : 'encounter_creature_spell_prepared_deleted';
        for (const row of raw[table] as Row[]) {
            const id = readItemField(row, key);
            if (table === 'pictures' ? fromBaseUuid(id) : Number.isSafeInteger(id) && Number(id) > 0)
                plan.writes.push({ table, op: 'update', row: { [key]: id, [deleted]: 1 }, before: { [deleted]: int(readItemField(row, deleted)) } });
        }
    }
    const floor = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [];
    for (const body of floor) if (upper(readInventoryField(body, 'inventory_encounter_creature_id')) === oldId) {
        plan.writes.push({ table: 'inventory', op: 'update', row: { inventory_id: readInventoryField(body, 'inventory_id'), inventory_deleted: 1, item_qty: 0 },
            before: { inventory_deleted: int(readInventoryField(body, 'inventory_deleted')), item_qty: Number(readInventoryField(body, 'item_qty') ?? 1) } });
    }
    const casterRows = Array.isArray(status.caster) ? status.caster as Row[] : [];
    for (const cast of casterRows) {
        const targetId = upper(readItemField(cast, 'status_target_id')), itemId = upper(readItemField(cast, 'status_item_id'));
        if (targetId === oldId || !read.casterDeathStatusIds?.includes(itemId)) continue;
        const target = roster.find(row => idOf(row) === targetId);
        if (!target || !fromBaseRecord(target.statuses) || !Array.isArray(target.statuses.target)) {
            (plan.logs ??= []).push({ kind: 'janus_unported_skip', name: String(saved(victim, 'encounter_creature_name') ?? 'Creature'), detail: 'Caster-death cleanup: target inputs are not held.' });
            continue;
        }
        const connection = String(readItemField(cast, 'status_connected_id') ?? '');
        for (const peer of target.statuses.target as Row[]) if (String(readItemField(peer, 'status_connected_id') ?? '') === connection) {
            const id = readItemField(peer, 'status_id');
            if (fromBaseUuid(id) && !plan.writes.some(write => write.table === 'statuses' && write.row.status_id === id)) plan.writes.push({ table: 'statuses', op: 'update',
                row: { status_id: id, status_deleted: 1 }, before: { status_deleted: int(readItemField(peer, 'status_deleted')), status_target_id: targetId } });
        }
    }
    return admitSpawnRowPlan(plan);
}

/** lair sync never resurrects a GM-removed row, and never adds one per creature sharing a lair.
 *  `lairIds` reads one roster creature's lairs: a source package carries only its own roots' closure, so a caller holding other
 *  creatures supplies a reader that can see them (an added Bat's package cannot resolve a held Zombie's lineage carrier). */
export function planLairRows(encounterId: string, encounter: Row, retiredLairItemIds: readonly string[], read: SpawnReaders,
    lairIds: (creature: Row) => readonly string[] = creature => read.entries(creature, 'lair').map(readLayerListEntryUuid)): SpawnRowPlan {
    const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
    const present = new Set([...retiredLairItemIds.map(upper), ...roster.filter(row => saved(row, 'item_type') === 'lair')
        .map(row => upper(saved(row, 'item_id')))]);
    const plan = emptySpawnPlan(), working = { ...encounter, encounter_creatures_arr: [...roster] };
    for (const creature of roster) {
        if (saved(creature, 'item_type') === 'lair' || saved(creature, 'item_type') === 'player_character') continue;
        for (const id of lairIds(creature)) {
            if (!fromBaseUuid(id) || present.has(upper(id))) continue;
            present.add(upper(id));
            try {
                if (readItemField(read.item(id), 'item_type') !== 'lair') continue;
                const addition = planCreatureSpawn(encounterId, working, { itemId: id, loot: false, point: { x: 0, y: 0, z: 0 },
                    instanceId: spawnRowIdentity(`lair|${upper(encounterId)}|${upper(id)}`) }, read);
                plan.writes.push(...addition.writes); (plan.logs ??= []).push(...addition.logs ?? []);
                (working.encounter_creatures_arr as Row[]).push(...addition.writes.filter(write => write.table === 'encounter_creatures').map(write => ({ encounter_creatures: write.row })));
            } catch (error) {
                (plan.logs ??= []).push({ kind: 'janus_unported_skip',
                    name: String(saved(creature, 'encounter_creature_name') ?? readItemField(creature, 'item_name') ?? 'Creature'),
                    detail: `server-lair:${String(error instanceof Error ? error.message : error)}` });
            }
        }
    }
    return admitSpawnRowPlan(plan);
}

/** The spell resolver's descriptor becomes complete saved rows before the request is queued. */
export function planSummonRows(encounterId: string, encounter: Row, caster: Row, abilityId: string,
    descriptors: readonly SummonPlan[], read: SpawnReaders): SpawnRowPlan {
    const plan = emptySpawnPlan(), working = { ...encounter };
    for (const descriptor of descriptors) {
        const roster = Array.isArray(working.encounter_creatures_arr) ? working.encounter_creatures_arr as Row[] : [];
        const controller = descriptor.caster_id ? roster.find(row => idOf(row) === upper(descriptor.caster_id)) : caster;
        if (!controller) throw new Error('spawn:controller-not-held');
        const corpse = descriptor.corpse ? roster.find(row => idOf(row) === upper(descriptor.corpse)) : null;
        if (descriptor.corpse && !corpse) throw new Error('spawn:corpse-not-held');
        const point = corpse ? positionOf(corpse) : descriptor.point;
        if (corpse && (saved(corpse, 'item_type') === 'player_character' || int(saved(corpse, 'encounter_creature_deleted')) !== 0)) throw new Error('spawn:corpse-retired-or-player');
        const request = { itemId: descriptor.item_id, monsterTemplateId: descriptor.monster_template_id,
            controllerId: idOf(controller), team: descriptor.team, summonType: descriptor.summon_type_id, point, floorPoint: false, private: 0, loot: false };
        const addition = corpse ? planTransformRows(encounterId, working, { ...request, victimId: idOf(corpse), equipInventory: true }, read)
            : planCreatureSpawn(encounterId, working, request, read);
        const row = addition.writes.find(write => write.table === 'encounter_creatures' && write.op === 'insert')!.row;
        const id = String(readItemField(row, 'encounter_creature_id'));
        if (corpse) {
            const appearance = saved(corpse, 'appearance');
            if (appearance != null && appearance !== '') row.appearance = typeof appearance === 'string' ? appearance : JSON.stringify(appearance);
            const body = saved(corpse, 'body_appearance');
            if (body != null && body !== '') row.body_appearance = typeof body === 'string' ? body : JSON.stringify(body);
            else {
                const projected = deriveCreatureBodyAppearance(appearance);
                if (projected) row.body_appearance = JSON.stringify(projected);
            }
            const raw = fromBaseRecord(corpse._entity_base) && fromBaseRecord(corpse._entity_base.base) ? corpse._entity_base.base : corpse;
            const meshes = Array.isArray(raw.meshes) ? raw.meshes as Row[] : [];
            if (meshes.some(mesh => ['ft3d_avatar', 'ft3d_avatar_clothed'].includes(String(readItemField(mesh, 'mesh_variant_key')))))
                throw new Error('spawn:avatar-binding-plan-required');
        }
        if (descriptor.tag) appendSpawnStatus(addition, read, id, idOf(controller), read.summonTag, descriptor.ability_id || abilityId, descriptor.tag);
        // Ordinary status uniqueness is target/item, so the first spawned body owns the one ability-as-status source for this cast.
        // New casts/casters retain distinct physical targets; all siblings end through the same native connection cascade.
        if (descriptor.maintaining_source) appendSpawnStatus(addition, read, id, idOf(controller), descriptor.ability_id || abilityId, '', descriptor.tag);
        const source = read.creature(descriptor.item_id, descriptor.monster_template_id);
        for (const entry of read.entries(source, 'passive_ability')) if (fromBaseRecord(entry) && upper(readItemField(entry, 'item_id')) === upper(read.etherId)) {
            const charges = fromBaseRecord(entry.charges) ? entry.charges : {};
            const max = readItemField(charges, 'max');
            if (Number.isFinite(Number(max)) && Number(max) > 0) addition.writes.push({ table: 'charges', op: 'insert', row: {
                charge_id: read.uuid(), charge_holder: id, item_id: read.etherId, charges: descriptor.in_combat ? 0 : int(max), charge_private: 0, charge_deleted: 0,
            } });
        }
        plan.writes.push(...addition.writes);
        const name = String(readItemField(row, 'encounter_creature_name') ?? readItemField(source, 'item_name') ?? 'Creature');
        (plan.logs ??= []).push(...addition.logs ?? [], { kind: 'summoned_creature', name, detail: '', actor_id: idOf({ encounter_creatures: row }),
            arrival: summonArrival(source, descriptor.ability_id || abilityId, read) });
        working.encounter_creatures_arr = [...roster.filter(row => !corpse || idOf(row) !== idOf(corpse)), { encounter_creatures: row }];
    }
    return admitSpawnRowPlan(plan);
}
